import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const defaultPinnedAreaWidth = 300;
const minPinnedAreaWidth = 260;
const maxPinnedAreaWidth = 600;

export const usePinnedAreaGlue = () => {
  const [pinnedItems, setPinnedItems] = useState<string[]>([]);
  const [pinnedAreaWidth, setPinnedAreaWidth] = useState<number>(defaultPinnedAreaWidth);
  const [isResizingPinnedArea, setIsResizingPinnedArea] = useState<boolean>(false);
  const itemAreaRef = useRef<HTMLDivElement>(null);

  const pinnedItemsSet = useMemo(() => new Set(pinnedItems), [pinnedItems]);

  const addPinnedItem = useCallback((itemId: string, pinned: boolean) => {
    setPinnedItems((currentPinnedItems) => {
      if (pinned) {
        return [...currentPinnedItems, itemId];
      }
      return currentPinnedItems.filter(id => id !== itemId);
    });
  }, [setPinnedItems]);

  const removePinnedItem = useCallback((itemId: string) => {
    setPinnedItems((currentPinnedItems) => currentPinnedItems.filter(id => id !== itemId));
  }, [setPinnedItems]);

  const retainPinnedItems = useCallback((allowedItemIds: string[]) => {
    const allowedIds = new Set(allowedItemIds);
    setPinnedItems((currentPinnedItems) => currentPinnedItems.filter(id => allowedIds.has(id)));
  }, [setPinnedItems]);

  useEffect(() => {
    if (!isResizingPinnedArea) {
      return;
    }

    const mouseMoveHandler = (e: MouseEvent) => {
      const itemAreaElement = itemAreaRef.current;
      if (!itemAreaElement) {
        return;
      }

      const itemAreaRect = itemAreaElement.getBoundingClientRect();
      const maxAllowedPinnedWidth = Math.min(maxPinnedAreaWidth, itemAreaRect.width * 0.5);
      const effectiveMinPinnedWidth = Math.min(minPinnedAreaWidth, maxAllowedPinnedWidth);
      const desiredPinnedWidth = itemAreaRect.right - e.clientX;
      const clampedPinnedWidth = Math.min(maxAllowedPinnedWidth, Math.max(effectiveMinPinnedWidth, desiredPinnedWidth));
      setPinnedAreaWidth(clampedPinnedWidth);
    };

    const mouseUpHandler = () => {
      setIsResizingPinnedArea(false);
    };

    window.addEventListener('mousemove', mouseMoveHandler);
    window.addEventListener('mouseup', mouseUpHandler);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      window.removeEventListener('mousemove', mouseMoveHandler);
      window.removeEventListener('mouseup', mouseUpHandler);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingPinnedArea]);

  const startResizePinnedArea = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsResizingPinnedArea(true);
  }, [setIsResizingPinnedArea]);

  return {
    pinnedItems,
    pinnedItemsSet,
    pinnedAreaWidth,
    isResizingPinnedArea,
    itemAreaRef,
    addPinnedItem,
    removePinnedItem,
    retainPinnedItems,
    startResizePinnedArea
  };
};
